import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { LocationQueryRaw, RouteLocationRaw } from 'vue-router';
import { binderReviewService } from '@/services/v2/BinderReviewService';
import type { BinderReviewItem, BinderReviewQuery, BinderReviewSource } from '@/types/BinderReview';

export interface BinderReviewCurrent {
  source: BinderReviewSource;
  promptResultId?: number;
  submissionId?: number;
}

/**
 * The filters a review page inherited from the list it was opened from, as route query
 * params. Carrying them means Back/Next steps through the set the reviewer was looking
 * at rather than jumping to a row they had filtered away.
 */
export function binderReviewRouteQuery(filters: BinderReviewQuery): LocationQueryRaw {
  const query: LocationQueryRaw = {};
  if (filters.status) {
    query.status = filters.status;
  }
  if (filters.source) {
    query.source = filters.source;
  }
  if (filters.search?.trim()) {
    query.search = filters.search.trim();
  }
  return query;
}

/**
 * The account's binder extractions as one ordered sequence, so a review page can say
 * where it sits in the client's results and step through them.
 *
 * The sequence is also the account boundary: the prompt-result endpoints are
 * account-agnostic, so a page that cannot find its result here must not open it. That
 * makes the filters load-bearing -- a page opened under a filter is guarded by the
 * filtered list, which is a subset of the account's, so it can only ever narrow.
 */
export function useBinderReviewSequence(
  accountId: MaybeRefOrGetter<string | number>,
  current: MaybeRefOrGetter<BinderReviewCurrent | null>,
  filters: MaybeRefOrGetter<BinderReviewQuery> = () => ({}),
) {
  const items = ref<BinderReviewItem[]>([]);
  const truncated = ref(false);
  const isLoading = ref(false);
  const loadFailed = ref(false);

  async function load() {
    isLoading.value = true;
    try {
      // Unpaged on purpose: Back/Next has to know where the sequence ends.
      const response = await binderReviewService.list(toValue(accountId), toValue(filters));
      items.value = response.items;
      truncated.value = response.truncated;
      loadFailed.value = false;
    } catch {
      // Navigation is an extra, not the reason the page exists: a failed list hides the
      // controls rather than blocking the review.
      items.value = [];
      truncated.value = false;
      loadFailed.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  // Only results that exist can be opened; a still-processing binder has nothing to show,
  // and a deleted or inaccessible one has already dropped out of the account's list.
  const sequence = computed(() => items.value.filter((item) => item.promptResultId != null));

  const matches = (item: BinderReviewItem, key: BinderReviewCurrent) =>
    item.source === key.source &&
    (key.promptResultId != null
      ? item.promptResultId === key.promptResultId
      : item.submissionId === key.submissionId);

  const currentItem = computed(() => {
    const key = toValue(current);
    if (!key) {
      return null;
    }
    return items.value.find((item) => matches(item, key)) ?? null;
  });

  const currentIndex = computed(() => {
    const key = toValue(current);
    if (!key) {
      return -1;
    }
    return sequence.value.findIndex((item) => matches(item, key));
  });

  const prevItem = computed(() => (currentIndex.value > 0 ? sequence.value[currentIndex.value - 1] : null));
  const nextItem = computed(() =>
    currentIndex.value >= 0 && currentIndex.value < sequence.value.length - 1
      ? sequence.value[currentIndex.value + 1]
      : null,
  );

  const position = computed(() =>
    currentIndex.value >= 0 ? `${currentIndex.value + 1} of ${sequence.value.length}` : '',
  );

  function routeFor(item: BinderReviewItem): RouteLocationRaw {
    const id = String(toValue(accountId));
    const query = binderReviewRouteQuery(toValue(filters));
    return item.source === 'external'
      ? {
          name: 'account-external-policy-binder-review',
          params: { id, submissionId: String(item.submissionId) },
          query,
        }
      : { name: 'account-binder-review', params: { id, promptResultId: String(item.promptResultId) }, query };
  }

  // A changed filter is a different sequence, so it reloads the same way a changed account
  // does. Watched as a serialized key rather than the object itself: the query is rebuilt
  // on every read, so watching it directly would reload on every tick and leave the page
  // reading a half-loaded sequence.
  watch(
    () => `${toValue(accountId)}|${JSON.stringify(binderReviewRouteQuery(toValue(filters)))}`,
    load,
    { immediate: true },
  );

  return {
    items,
    sequence,
    truncated,
    isLoading,
    loadFailed,
    currentItem,
    currentIndex,
    prevItem,
    nextItem,
    position,
    routeFor,
    reload: load,
  };
}
